import { ErrorCode, ConsumerLookupAction, ConsumerLookupCohort, ConsumerLookupRecognizedBy } from '@afterpaytouch/portal-api/types'
import { createSlice, isAnyOf, PayloadAction, Slice } from '@reduxjs/toolkit'
import { v4 as uuid } from 'uuid'
import {
  authLogin,
  authLogout,
  check2faDob,
  checkPersistentLogin,
  closeCheckout,
  confirmRegistrationMobile,
  createFingerprint,
  emailLookup,
  finaliseAccountDetails,
  getCheckout,
  loginWithOneTimePassword,
  mobileLookup,
  newPersistentSession,
  revokePersistentSession,
  requestTokenFromApp,
  sendOneTimePassword,
  setSessionExpired,
} from '../../'
import { Route } from '../../../config/router'
import {
  AFTERPAY_CHECKOUT_LOGIN_TOKEN,
  authenticate,
  Authentication,
  AuthenticationErrors,
  CASH_CHECKOUT_TOKEN,
  FingerPrint,
  isPublicResponse,
  MobileLogin,
  persistentSession,
  unauthenticate,
  VerificationMethod,
} from '../../../model'
import { PURGE } from 'redux-persist'

export interface AuthenticationState {
  status: Authentication
  error?: AuthenticationErrors
  profilingId: string | null
  fingerprint: FingerPrint | null
  enableOTP: boolean
  autoSendOTP: boolean
  enabled2faMethods: Record<VerificationMethod, boolean>
  initial2faMethod?: VerificationMethod
  autoSend2fa: boolean
  isTrustedCheckout: boolean
  mobileLogin: MobileLogin
  authToken?: string
  afterpayCheckoutLoginToken?: string
  cashCheckoutToken?: string
  isPrivateBrowsingMode: boolean
  // True while a remember-me mint request is in flight; cleared by logout/closeCheckout so a late
  // response cannot mark a logged-out store as persistent.
  mintInFlight: boolean
  // True only when the session was *silently recognized* at resume via checkPersistentLogin (an
  // existing Auto Remember Me cookie), as opposed to a fresh interactive login that later mints a
  // persistent session on the LoggedIn page. The "Welcome back" recognition transient gates on this
  // so it never shows — and never logs Viewed Auto Remember Me Page — on a normal password/OTP login,
  // where newPersistentSession.fulfilled would otherwise flip status.persistent to true mid-page.
  recognizedViaPersistentLogin: boolean
}

export const userInitialState: AuthenticationState = {
  status: unauthenticate(),
  profilingId: uuid(),
  fingerprint: null,
  enableOTP: true,
  autoSendOTP: true,
  enabled2faMethods: {
    SMS: true,
    EMAIL: false,
  },
  autoSend2fa: true,
  isTrustedCheckout: false,
  mobileLogin: {
    isMobileLogin: false,
    dateOfBirthVerificationRequired: false,
    // @ts-expect-error: OPERATION BLEED STOPPER
    verifiedRoute: null,
    hasSeenChallenge: false,
  },
  isPrivateBrowsingMode: false,
  mintInFlight: false,
  recognizedViaPersistentLogin: false,
}

const authErrors: Partial<Record<ErrorCode, AuthenticationErrors>> = {
  [ErrorCode.AccountLocked]: AuthenticationErrors.AccountLocked,
  [ErrorCode.AccountDisabled]: AuthenticationErrors.AccountDisabled,
  [ErrorCode.AuthenticationFailure]: AuthenticationErrors.IncorrectPassword,
  [ErrorCode.AuthenticationFailureMaxAttempts]: AuthenticationErrors.FailureMaxAttempts,
  [ErrorCode.TooManyOTPRequests]: AuthenticationErrors.TooManyOTPRequests,
  [ErrorCode.PaywatchCheckFailed]: AuthenticationErrors.PaywatchCheckFailed,
}

const slice: Slice = createSlice({
  name: 'auth',
  initialState: userInitialState,
  reducers: {
    setMobileLoginVerifiedRoute(state: AuthenticationState, action: PayloadAction<Route>) {
      state.mobileLogin.verifiedRoute = action.payload
    },
    setIsMobileLogin(state: AuthenticationState, action: PayloadAction<boolean>) {
      state.mobileLogin.isMobileLogin = action.payload
    },
    setAuthToken(state: AuthenticationState, action: PayloadAction<string>) {
      state.authToken = action.payload
    },
    // Inline Cash login (CASHDAP-3104): RedeemLoginIntent returns a checkout session token but,
    // unlike the password/OTP/finalise login reducers, no thunk flips `status.authenticated`.
    // Store the token AND mark the session authenticated so authenticated-only checkout behaviour
    // (close-flow logout, public-page back-nav guard) runs after a successful inline Cash login.
    setCashSessionAuthenticated(state: AuthenticationState, action: PayloadAction<string>) {
      state.authToken = action.payload
      state.status = authenticate(false, false)
    },
    setIsPrivateBrowsingMode(state: AuthenticationState, action: PayloadAction<boolean>) {
      state.isPrivateBrowsingMode = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestTokenFromApp.fulfilled, (state, action) => {
        if (action.payload.type === AFTERPAY_CHECKOUT_LOGIN_TOKEN) {
          state.afterpayCheckoutLoginToken = action.payload.payload
          state.cashCheckoutToken = undefined
        } else if (action.payload.type === CASH_CHECKOUT_TOKEN) {
          state.cashCheckoutToken = action.payload.payload
          state.afterpayCheckoutLoginToken = undefined
        }
      })
      .addCase(requestTokenFromApp.rejected, (state) => {
        state.afterpayCheckoutLoginToken = undefined
        state.cashCheckoutToken = undefined
      })
      .addCase(authLogin.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(authLogin.rejected, (state, action) => {
        state.status = unauthenticate()
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }
      })
      .addCase(authLogin.fulfilled, (state, action) => {
        const isPersistentSession = action.meta.arg['remember-me']
        state.enabled2faMethods.EMAIL = action.payload.user.shouldAllowEmail
        state.status = authenticate(action.payload.user.requires2fa, isPersistentSession, isPersistentSession)
      })
      .addCase(authLogout.fulfilled, (state) => {
        state.status = unauthenticate()
        state.authToken = undefined
        // Trusted/OAuth provenance belongs to the session being ended; a later interactive login
        // in the same tab must not inherit it (it would wrongly skip the remember-me mint).
        state.isTrustedCheckout = false
        state.mintInFlight = false
      })
      .addCase(checkPersistentLogin.fulfilled, (state, action) => {
        const { active, verify, allowEmail2Fa, isTrustedCheckout } = action.payload
        state.status = persistentSession(active, verify)
        if (verify) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.enabled2faMethods.EMAIL = allowEmail2Fa
        }
        state.isTrustedCheckout = isTrustedCheckout
        // Silent recognition: an active persistent-login resume is the only path that marks a session
        // as recognized. A later mint (newPersistentSession) deliberately leaves this false.
        state.recognizedViaPersistentLogin = active
      })
      .addCase(createFingerprint.fulfilled, (state, action) => {
        state.fingerprint = action.payload
      })
      .addCase(newPersistentSession.pending, (state) => {
        state.mintInFlight = true
      })
      .addCase(newPersistentSession.fulfilled, (state, action) => {
        // Ignore a response that lands after logout/closeCheckout cancelled interest in it — a
        // late fulfil must not mark a logged-out store as persistent.
        if (state.mintInFlight) {
          state.status.persistent = action.payload
          state.mintInFlight = false
        }
      })
      .addCase(newPersistentSession.rejected, (state) => {
        state.mintInFlight = false
      })
      .addCase(revokePersistentSession.pending, (state) => {
        // CASHDAP-3258: clear persistence the moment the opt-out revoke is dispatched — the user's
        // intent is decisive, not the server outcome. If the best-effort call then fails, the
        // device-side opt-out gates still block resume, and clearing eagerly means a close-checkout
        // during the in-flight revoke takes the full authLogout path (useCloseCheckout skips logout
        // for persistent sessions) instead of leaving the session alive. The session itself stays
        // authenticated. `.fulfilled`/`.rejected` cases are intentionally absent: portalAction
        // swallows errors as payload false, and there is nothing further to apply on success.
        if (state.status.authenticated) {
          state.status.persistent = false
        }
      })
      .addCase(emailLookup.pending, (state, action) => {
        state.mobileLogin = {
          // if it's true, mean the email from the mobile login flow
          isMobileLogin: action.meta.arg.isFromRegistration,
          // @ts-expect-error: OPERATION BLEED STOPPER
          mobile: null,
          dateOfBirthVerificationRequired: false,
          hasSeenChallenge: false,
        }
      })
      .addCase(emailLookup.fulfilled, (state, action) => {
        state.enabled2faMethods.SMS = action.payload.smsVerificationEnabled
        state.status.verificationRequired = action.payload.smsVerificationRequired
      })
      .addCase(mobileLookup.pending, (state) => {
        state.mobileLogin = {
          isMobileLogin: true,
          dateOfBirthVerificationRequired: false,
          // @ts-expect-error: OPERATION BLEED STOPPER
          verifiedRoute: null,
          hasSeenChallenge: false,
        }
      })
      .addCase(mobileLookup.fulfilled, (state, action) => {
        if (isPublicResponse(action.payload)) {
          return
        }
        const { passwordExists, dateOfBirthVerificationRequired, isFromInitialiseCheckout = false, existing } = action.payload
        const verifiedRoute = passwordExists ? null : Route.VERIFY
        state.mobileLogin = {
          isMobileLogin: isFromInitialiseCheckout ? existing : true,
          dateOfBirthVerificationRequired,
          // @ts-expect-error: OPERATION BLEED STOPPER
          verifiedRoute,
          hasSeenChallenge: false,
        }
        state.status.verificationRequired = action.payload.smsVerificationRequired
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        if (typeof action.payload.consumerLookup !== 'undefined') {
          const { cohort, action: lookupAction, recognizedBy } = action.payload.consumerLookup
          const verifiedRoute = lookupAction !== ConsumerLookupAction.SET_PASSWORD ? null : Route.VERIFY
          state.mobileLogin = {
            isMobileLogin: recognizedBy === ConsumerLookupRecognizedBy.MOBILE && cohort === ConsumerLookupCohort.existing_consumer,
            dateOfBirthVerificationRequired: lookupAction === ConsumerLookupAction.DATE_OF_BIRTH_VERIFICATION,
            // @ts-expect-error: OPERATION BLEED STOPPER
            verifiedRoute,
            hasSeenChallenge: false,
          }
          state.status.verificationRequired = lookupAction === ConsumerLookupAction.SMS_VERIFICATION
        }
      })
      .addCase(check2faDob.fulfilled, (state) => {
        // once DOB verification is passed, the dateOfBirthVerificationRequired needs to be set to false to prevent re-prompt
        state.mobileLogin.dateOfBirthVerificationRequired = false
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.mobileLogin.verifiedRoute = null
        state.mobileLogin.hasSeenChallenge = true
      })
      .addCase(sendOneTimePassword.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(sendOneTimePassword.rejected, (state, action) => {
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }

        if (state.error === AuthenticationErrors.TooManyOTPRequests) {
          state.enableOTP = false
        }
      })
      .addCase(sendOneTimePassword.fulfilled, (state) => {
        state.error = undefined
        // CHECKOUT-591: Quick fix to disable CHECKOUT-563
        // state.autoSendOTP = false
      })
      .addCase(loginWithOneTimePassword.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(loginWithOneTimePassword.rejected, (state, action) => {
        state.status = unauthenticate()
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }

        if (state.error === AuthenticationErrors.FailureMaxAttempts) {
          state.enableOTP = false
        }
      })
      .addCase(loginWithOneTimePassword.fulfilled, (state, action) => {
        state.status = authenticate(action.payload.user.requires2fa, false)
      })
      .addCase(confirmRegistrationMobile.fulfilled, (state) => {
        state.status.verificationRequired = false
      })
      .addCase(finaliseAccountDetails.fulfilled, (state) => {
        state.status = authenticate(false, false)
      })
      .addCase(closeCheckout, (state) => {
        state.authToken = undefined
        state.mintInFlight = false
      })
      .addCase(PURGE, () => userInitialState)
      // recognizedViaPersistentLogin is set true ONLY by an active checkPersistentLogin resume. Any
      // other interactive-login start (password, OTP send/login) or session teardown (logout, close,
      // 401 session-expiry) must drop it, otherwise a stale flag from an earlier recognized session
      // can resurface the welcome-back transient on a fresh login whose mint flips persistent true on
      // LoggedIn. Centralized here so no individual login path can forget to clear it.
      .addMatcher(
        isAnyOf(
          authLogin.pending,
          authLogin.rejected,
          sendOneTimePassword.pending,
          loginWithOneTimePassword.pending,
          loginWithOneTimePassword.rejected,
          authLogout.fulfilled,
          closeCheckout,
          setSessionExpired
        ),
        (state) => {
          state.recognizedViaPersistentLogin = false
        }
      )
  },
})

export const { setAuthToken, setIsMobileLogin, setMobileLoginVerifiedRoute, setIsPrivateBrowsingMode, setCashSessionAuthenticated } = slice.actions

export default slice.reducer
